import { forwardRef, useState, useEffect, useMemo } from 'react';
import type { Ref, PropsWithoutRef, SyntheticEvent } from 'react';
import styled, { css } from 'styled-components';
import { rgba } from 'polished';

import Image from '../Image';
import type {
  BaseProps,
  ForwardRefForwardPropsComponent,
  PropsWithDefaults,
  NoChildrenProp,
  OmitStrict,
  TestIdProp
} from '../../types';
import { defaultThemeProp } from '../../theme';
import Icon, { StyledIcon } from '../Icon';
import { Alert } from '../Badges';
import { StyledBareButton } from '../Button/BareButton';
import { useI18n, useTestIds } from '../../hooks';
import { tryCatch, withTestIds } from '../../utils';
import { readableColor, calculateForegroundColor } from '../../styles';

import { getAvatarTestIds } from './Avatar.test-ids';

export interface AvatarProps extends BaseProps, NoChildrenProp, TestIdProp {
  /** The name of the identity the Avatar represents. */
  name: string;
  /** Content of the Avatar as an image source url. */
  imageSrc?: string;
  /** An icon to render as the content of the avatar. imageSrc will take precedence if passed. */
  icon?: string;
  /** Optional color for the icon to be rendered */
  color?: string;
  /** Optional background color for the avatar. Will be auto computed if not passed */
  backgroundColor?: string;
  /**
   * Shape of the Avatar.
   * @default 'circle'
   */
  shape?: 'circle' | 'squircle';
  /**
   * Avatar size.
   * @default 'm'
   */
  size?: 's' | 'm' | 'l' | 'xl';
  /** If defined, will render status icon on the avatar. */
  status?: 'active';
  /** Optional error handler in the event an image fails to load. */
  onImageError?: (err: SyntheticEvent<HTMLImageElement>) => void;
  /** Ref for the wrapping element. */
  ref?: Ref<HTMLDivElement | HTMLImageElement>;
}

type AvatarPropsWithDefaults = PropsWithDefaults<AvatarProps, 'shape' | 'size'>;

const sizeToRem = {
  s: 1,
  m: 2,
  l: 2.5,
  xl: 4
};

const fontSizeToRem = {
  s: 0.5625,
  m: 0.8125,
  l: 1,
  xl: 1.5
};

export const StyledAvatarImage = styled(Image)`
  min-height: 100%;
  min-width: 100%;
  aspect-ratio: 1 / 1;
  object-fit: cover;
  border-radius: inherit;
  height: fit-content;
  width: fit-content;
`;

export const StyledAvatar = styled.div<
  OmitStrict<AvatarPropsWithDefaults, 'name' | 'children'> & {
    hasSrc?: boolean;
    hasBrokenImage?: boolean;
  }
>(
  ({
    hasBrokenImage,
    hasSrc,
    size,
    shape,
    color,
    backgroundColor,
    theme: {
      base: {
        palette: { 'foreground-color': foregroundColor, 'primary-background': primaryBackground },
        'border-radius': baseBorderRadius
      },
      components: {
        avatar: {
          background: avatarBackground,
          'background-color': avatarBackgroundColor,
          'foreground-color': avatarForegroundColor
        },
        icon: { 'border-radius-multiplier': borderRadiusMultiplier },
        button: { 'focus-shadow': focusShadow }
      }
    }
  }) => {
    const sizeRem = sizeToRem[size];
    const fontSize = fontSizeToRem[size];
    let bgColor =
      hasSrc && !hasBrokenImage ? primaryBackground : backgroundColor || avatarBackgroundColor;
    if (color && !backgroundColor && !(hasSrc && !hasBrokenImage))
      bgColor = tryCatch(
        () => readableColor(color),
        () => avatarBackgroundColor
      );

    const shadowColor = tryCatch(() => rgba(foregroundColor, 0.1));
    const iconColor = color || calculateForegroundColor(bgColor, avatarForegroundColor);

    return css`
      position: relative;
      flex-shrink: 0;
      background: ${avatarBackground};
      color: ${iconColor};
      width: ${sizeRem}rem;
      height: ${sizeRem}rem;
      min-width: ${sizeRem}rem;
      min-height: ${sizeRem}rem;
      font-size: ${fontSize}rem;
      text-transform: uppercase;
      line-height: ${sizeRem}rem;
      text-align: center;
      vertical-align: top;
      display: inline-block;
      border-radius: 100%;
      user-select: none;
      -webkit-user-select: none;

      ${shape === 'squircle' &&
      css`
        border-radius: calc(${borderRadiusMultiplier} * ${baseBorderRadius});
      `}

      ${StyledIcon} {
        width: 50%;
        height: 100%;
        vertical-align: initial;
      }

      &::after {
        content: '';
        position: absolute;
        inset: 0;
        border-radius: inherit;
        pointer-events: none;
        box-shadow: inset 0 0 0 0.0625rem ${shadowColor};
      }

      ${StyledBareButton}:focus > & {
        box-shadow: ${focusShadow};
      }
    `;
  }
);

StyledAvatar.defaultProps = defaultThemeProp;

export const StyledStatus = styled(Alert)<Pick<AvatarPropsWithDefaults, 'size'>>(({
  size,
  theme
}) => {
  /** Scales alert badge dimensions based on size of avatar */
  const sizeDimensions = (0.625 * sizeToRem[size]) / sizeToRem.m;

  return css`
    position: absolute;
    height: ${sizeDimensions}rem;
    width: ${sizeDimensions}rem;
    inset-block-end: 0;
    inset-inline-end: 0;
    z-index: 1;
    animation: pop calc(${theme.base.animation.speed} * 2) cubic-bezier(1, 1.65, 0.3, 1.24);

    @keyframes pop {
      0% {
        opacity: 0;
        transform: scale(0);
      }
      50% {
        opacity: 1;
      }
      100% {
        transform: scale(1);
      }
    }
  `;
});

StyledStatus.defaultProps = defaultThemeProp;

/**
 * Helper function to construct a two or one letter initial from the title prop.
 * This text will be displayed if no valid image source is provided.
 * @param name
 * @param size
 */
const getInitials = (name: AvatarProps['name'], size: AvatarProps['size']) => {
  if (!name) {
    return '';
  }

  if (size === 's') {
    return name[0];
  }

  const words = name.split(/\s+/);

  return `${words[0][0] ?? ''}${words[1]?.[0] ?? ''}`;
};

/**
 * Generally, avatars are used to distinguish between different operators and
 * objects when they are shown in a list or in a small space. Users of applications
 * can upload a custom avatar of their choice.
 */
const Avatar: ForwardRefForwardPropsComponent<AvatarProps> = forwardRef(function Avatar(
  props: PropsWithoutRef<AvatarProps>,
  ref: AvatarProps['ref']
) {
  const {
    testId,
    shape = 'circle',
    size = 'm',
    imageSrc,
    icon,
    name: nameProp,
    status,
    onImageError,
    ...restProps
  } = props;

  const name = nameProp.trim();

  const [hasBrokenImage, setHasBrokenImage] = useState<undefined | boolean>();
  const t = useI18n();
  const testIds = useTestIds(testId, getAvatarTestIds);

  useEffect(() => {
    setHasBrokenImage(undefined);
  }, [imageSrc]);

  const content = useMemo(() => {
    if (icon) {
      return <Icon name={icon} />;
    }
    if (imageSrc && !hasBrokenImage) {
      return (
        <StyledAvatarImage
          src={imageSrc}
          alt={name}
          onError={(e: SyntheticEvent<HTMLImageElement>) => {
            setHasBrokenImage(true);
            onImageError?.(e);
          }}
          onLoad={() => {
            setHasBrokenImage(false);
          }}
        />
      );
    }
    return getInitials(name, size);
  }, [imageSrc, name, size, icon, hasBrokenImage, onImageError]);

  return (
    <StyledAvatar
      data-testid={testIds.root}
      role='img'
      hasSrc={!!imageSrc}
      hasBrokenImage={hasBrokenImage}
      shape={shape}
      size={size}
      ref={ref}
      aria-label={`${name}${status ? ` ${t('status')} ${t(status)}` : ''}`}
      {...restProps}
    >
      {content}
      {status === 'active' && <StyledStatus size={size} variant='success' />}
    </StyledAvatar>
  );
});

export default withTestIds(Avatar, getAvatarTestIds);
